"use client";

import { useProgress } from "@react-three/drei";
import { AnimatePresence, motion, type Variants } from "motion/react";
import type React from "react";
import { useContext, useEffect, useRef, useState } from "react";
import { BsTriangle as DownwardArrow } from "react-icons/bs";
import BrandBanner from "@/assets/images/piperunner-banner.svg";
import BrandLogo from "@/assets/images/piperunner-logo.svg";
import { HeroContext } from "@/providers/hero-provider";
import { useStore } from "@/store";
import { Scrambler } from "./Scrambler";

const MINIMUM_LOADING_TIME = 3000;

const logoVariants: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

const textVariants: Variants = {
  initial: { opacity: 0, bottom: 0 },
  beforeLoading: { opacity: 1, bottom: 0, transition: { duration: 1 } },
  afterLoading: {
    opacity: 1,
    top: 0,
    transition: {
      duration: 1,
      when: "beforeChildren",
    },
  },
};

const underlineTextVariants: Variants = {
  initial: { opacity: 0 },
  afterLoading: {
    opacity: 1,
    transition: {
      duration: 1,
      when: "beforeChildren",
    },
  },
};

const underlineVariants: Variants = {
  initial: { translateX: "-100%" },
  afterLoading: {
    translateX: 0,
    transition: {
      duration: 1,
      when: "beforeChildren",
    },
  },
};

const underlineNorText: Variants = {
  initial: { translateY: "-100%" },
  afterLoading: { translateY: 0, transition: { duration: 1 } },
};

const Hero: React.FC = () => {
  const isHomeVisited = useStore((state) => state.isHomeVisited);

  /**
   * If progress finishes before the minimum loading time, wait for the minimum loading time,
   * else wait for the progress to finish.
   */
  const { progress, errors } = useProgress();
  const elapsedTime = useRef<number | null>(null);
  const currentTime = useRef<number | null>(null);

  const { onFinishLoadingAnimation } = useContext(HeroContext);

  const [isAnimationComplete, setIsAnimationComplete] = useState(isHomeVisited);
  const [shouldShowText, setShouldShowText] = useState(isHomeVisited);

  /**
   * Raf is not triggered when the user has already visited the page.
   * This is to avoid the animation loop.
   */
  useEffect(() => {
    if (elapsedTime.current === null && currentTime.current === null) {
      elapsedTime.current = 0;
      currentTime.current = Date.now();
    }

    const updateElapsedTime = () => {
      const delta = Date.now() - currentTime.current!;
      elapsedTime.current! += delta;
      currentTime.current = Date.now();

      /**
       * Either the progress is 100% or there are errors, we can finish the animation.
       */
      if ((progress >= 100 || errors?.length > 0) && elapsedTime.current! >= MINIMUM_LOADING_TIME) {
        // onFinishLoadingAnimation(); // exit animation will trigger this
        // trigger exit animation
        setIsAnimationComplete(true);

        if (rafId) {
          cancelAnimationFrame(rafId);
        }
        return;
      }

      requestAnimationFrame(updateElapsedTime);
    };

    const rafId = !isAnimationComplete && requestAnimationFrame(updateElapsedTime);

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [errors?.length, isAnimationComplete, progress]);

  return (
    <>
      <div className="flex flex-col justify-center items-center h-full">
        <div className="flex-1 relative flex justify-center tablet:mb-20 mb-8">
          <motion.div
            variants={textVariants}
            /**
             * Once the initial value is set, it does not change,
             * we take advantage of this to set the initial value to "initial"
             * if isHomeVisited is false at the start and "afterLoading" if it is true. Even if this value changes mid animation, it will not affect the animation.
             */
            initial={isHomeVisited ? "afterLoading" : "initial"}
            animate={isAnimationComplete ? "afterLoading" : "beforeLoading"}
            className="absolute min-w-[200px] desktop:w-[70vw] tablet:w-[80vw] w-[90vw] max-w-desktop z-1 pt-6 h-fit"
          >
            {/* https://stackoverflow.com/questions/78041464/when-i-use-framer-motion-i-cant-get-the-whenbeforechildrfen-afterchildren-pr */}
            <div>
              <BrandBanner />
            </div>
            <motion.div
              variants={underlineTextVariants}
              className="absolute left-0 top-full mt-4 flex w-full overflow-hidden"
            >
              <div className="text-xs tablet:text-2xl font-bold text-center tablet:p-2 p-1 font-header rounded-tl-sm rounded-bl-sm rounded-br-sm bg-brand h-fit">
                {"ENGINEER // ARTIST // GAMER"}
              </div>
              <motion.div className="flex-1 flex" variants={underlineVariants}>
                <div className="bg-brand tablet:h-[4px] h-[2px] flex-1" />
                <motion.div
                  className="ml-2 font-header font-bold space-y-2 text-xs tablet:text-md items-stretch justify-center"
                  variants={underlineNorText}
                  onAnimationComplete={(animationName) => {
                    if (animationName === "afterLoading") {
                      setShouldShowText(true);
                    }
                  }}
                >
                  <div className="bg-brand tablet:p-2 p-1 text-center">NO</div>
                  <div className="bg-brand tablet:h-[4px] h-[2px] w-full" />
                  <div className="[writing-mode:vertical-lr] [text-orientation:upright] bg-brand tablet:p-2 p-1 text-center rounded-b-sm w-full">
                    HIMMEL
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
        <div className="flex-1">
          <AnimatePresence onExitComplete={onFinishLoadingAnimation}>
            {!isAnimationComplete && (
              <motion.div
                key="logo"
                variants={logoVariants}
                transition={{ duration: 1 }}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <BrandLogo className="tablet:w-80 tablet:h-80 w-32 h-32" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      {shouldShowText && (
        <motion.div
          className="absolute left-0 bottom-header-gap"
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          transition={{
            duration: 1,
          }}
        >
          <div className="tablet:mb-6 tablet:ml-6 ml-2 mb-4 flex items-stretch overflow-hidden">
            <motion.div
              className="tablet:w-[4px] w-[2px] bg-chart-3"
              initial={
                isHomeVisited
                  ? { translateY: 0 }
                  : {
                      translateY: "100%",
                    }
              }
              animate={{
                translateY: 0,
              }}
              transition={{
                duration: 1,
                delay: 1,
              }}
            />
            <div className="flex flex-col">
              <div className="tablet:max-w-96 max-w-48 tablet:mt-20 tablet:pb-6 tablet:mr-20 tablet:ml-6 mt-4 mr-2 pb-2 ml-2">
                <Scrambler className="tablet:text-lg text-xs">
                  Corporate Netrunner since 2019. A disciple of Rache Bartmoss. A loyal member of the Bartmoss
                  collective.
                </Scrambler>
              </div>
              <motion.div
                initial={
                  isHomeVisited
                    ? {
                        translateX: 0,
                      }
                    : {
                        translateX: "-100%",
                      }
                }
                animate={{
                  translateX: 0,
                }}
                transition={{
                  duration: 1,
                  delay: 1,
                }}
                className="tablet:h-[4px] h-[2px] bg-chart-3"
              />
            </div>
          </div>
        </motion.div>
      )}
      {shouldShowText && (
        <motion.div
          className="absolute right-0 bottom-header-gap"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 2 }}
        >
          <motion.div className="mb-12 mr-6 rotate-180">
            <DownwardArrow size={36} className="animate-bounce fill-chart-3" />
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

export default Hero;
